<script setup>
// Fase 59 — histórico financeiro do card: cada venda registrada quando ele foi
// dado como ganho. Abre pelo chip "N oportunidades" (ou pelo próprio valor) no
// card do board, sem precisar abrir o card inteiro.
//
// Modal em vez de popover de propósito: o card do board acumula drag-and-drop,
// click-to-open e menus teleportados, e um popover ancorado dentro dele volta a
// disputar o mesmo clique (foi a origem dos bugs de menu em f175156fbf).
import { computed, toRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useOpportunityHistory } from '../composables/useOpportunityHistory';

const props = defineProps({
  card: { type: Object, required: true },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);
const { t } = useI18n();

// Carga, paginação, invalidação e formatação vivem no composable — esta tela e
// a aba dentro do card mostram o MESMO histórico e não podem divergir.
const {
  opportunities,
  loading,
  loadingMore,
  loadFailed,
  appendFailed,
  recordedCount,
  voidedCount,
  hasMore,
  totalValue,
  averageTicket,
  isEmpty,
  formatMoney,
  formatDate,
  entryTitle,
  voidLine,
  rowMeta,
  load,
  loadMore,
  reset,
  voiding,
  voidFailed,
  canVoidEntry,
  voidingEntry,
  voidReason,
  startVoid,
  cancelVoid,
  confirmVoid,
} = useOpportunityHistory(toRef(props, 'card'));

const cardTitle = computed(
  () =>
    props.card?.item_details?.title ||
    props.card?.title ||
    t('PIPELINE.UNTITLED_ITEM')
);

const hasLoadedTotal = computed(() => !loading.value && !loadFailed.value);

watch(
  () => props.show,
  visible => {
    if (!visible) return;
    reset();
    load();
  },
  { immediate: true }
);

const summaryLine = computed(() => {
  if (!recordedCount.value) return '';

  const parts = [t('PIPELINE.OPPORTUNITIES.SUMMARY', recordedCount.value)];
  if (voidedCount.value) {
    parts.push(t('PIPELINE.OPPORTUNITIES.VOIDED_SUMMARY', voidedCount.value));
  }
  parts.push(
    t('PIPELINE.OPPORTUNITIES.AVERAGE_TICKET', {
      value: formatMoney(averageTicket.value),
    })
  );
  return parts.join(' · ');
});
</script>

<template>
  <Modal :show="show" @close="emit('close')">
    <section
      class="flex max-h-[min(90vh,44rem)] max-w-lg flex-col overflow-hidden"
      data-modal="opportunity-history"
    >
      <header class="px-4 pt-5 sm:px-6 sm:pt-6">
        <div class="flex items-baseline justify-between gap-4">
          <h2 class="mb-1 pr-10 text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.OPPORTUNITIES.TITLE') }}
          </h2>
          <!-- Só mostra o total quando ele foi REALMENTE carregado: durante o
               carregamento ou depois de uma falha, "R$ 0,00" seria lido como
               "este cliente nunca comprou" — informação falsa sobre dinheiro. -->
          <span
            v-if="hasLoadedTotal"
            class="whitespace-nowrap text-heading-2 font-bold tabular-nums text-n-teal-11"
          >
            {{ formatMoney(totalValue) }}
          </span>
        </div>
        <p class="break-words text-body-main text-n-slate-11">
          {{ cardTitle }}
        </p>
        <p v-if="summaryLine" class="mt-1 text-label-small text-n-slate-10">
          {{ summaryLine }}
        </p>
      </header>

      <div class="overflow-y-auto px-4 py-4 sm:px-6">
        <p
          v-if="loading"
          class="py-6 text-center text-body-main text-n-slate-11"
        >
          {{ t('PIPELINE.OPPORTUNITIES.LOADING') }}
        </p>

        <p
          v-else-if="loadFailed"
          class="rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
          role="alert"
        >
          {{ t('PIPELINE.OPPORTUNITIES.LOAD_ERROR') }}
        </p>

        <p
          v-else-if="isEmpty"
          class="py-6 text-center text-body-main text-n-slate-11"
        >
          {{ t('PIPELINE.OPPORTUNITIES.EMPTY') }}
        </p>

        <ul v-else class="flex flex-col divide-y divide-n-weak">
          <li
            v-for="opportunity in opportunities"
            :key="opportunity.id"
            class="flex flex-col py-3"
            :class="{ 'opacity-60': opportunity.status === 'voided' }"
          >
            <div class="flex gap-3">
              <span
                class="w-20 flex-shrink-0 pt-0.5 text-label-small tabular-nums text-n-slate-10"
              >
                {{ formatDate(opportunity.won_at) }}
              </span>
              <span class="min-w-0 flex-1">
                <span class="block break-words text-label text-n-slate-12">
                  {{ entryTitle(opportunity) }}
                </span>
                <span
                  v-if="opportunity.status === 'voided'"
                  class="block text-label-small text-n-ruby-11"
                >
                  {{ voidLine(opportunity) }}
                </span>
                <span v-else class="block text-label-small text-n-slate-10">
                  {{ rowMeta(opportunity) }}
                </span>
              </span>
              <span
                class="whitespace-nowrap text-label font-bold tabular-nums"
                :class="
                  opportunity.status === 'voided'
                    ? 'text-n-slate-10 line-through'
                    : 'text-n-slate-12'
                "
              >
                {{ formatMoney(opportunity.total_value) }}
              </span>
              <Button
                v-if="canVoidEntry(opportunity)"
                variant="ghost"
                color="ruby"
                size="sm"
                :label="t('PIPELINE.OPPORTUNITIES.VOID')"
                :disabled="voiding !== null"
                @click="startVoid(opportunity)"
              />
            </div>

            <!-- Confirmação em dois passos, DENTRO do próprio item: estorno tira
                 a venda da receita na hora e não se desfaz. -->
            <div
              v-if="voidingEntry === opportunity.id"
              class="mt-3 rounded-lg bg-n-alpha-1 p-3"
            >
              <p class="mb-2 text-label-small text-n-slate-11">
                {{ t('PIPELINE.OPPORTUNITIES.VOID_HINT') }}
              </p>
              <Input
                v-model="voidReason"
                type="text"
                :placeholder="
                  t('PIPELINE.OPPORTUNITIES.VOID_REASON_PLACEHOLDER')
                "
                :disabled="voiding !== null"
              />
              <div class="mt-2 flex justify-end gap-2">
                <Button
                  variant="outline"
                  color="slate"
                  size="sm"
                  :label="t('PIPELINE.OPPORTUNITIES.VOID_CANCEL')"
                  :disabled="voiding !== null"
                  @click="cancelVoid"
                />
                <Button
                  color="ruby"
                  size="sm"
                  :label="t('PIPELINE.OPPORTUNITIES.VOID_CONFIRM')"
                  :is-loading="voiding === opportunity.id"
                  :disabled="voiding !== null"
                  @click="confirmVoid(opportunity)"
                />
              </div>
              <p
                v-if="voidFailed === opportunity.id"
                class="mt-2 text-sm text-n-ruby-11"
                role="alert"
              >
                {{ t('PIPELINE.OPPORTUNITIES.VOID_ERROR') }}
              </p>
            </div>
          </li>
        </ul>
        <p
          v-if="appendFailed"
          class="mt-3 text-center text-sm text-n-ruby-11"
          role="alert"
        >
          {{ t('PIPELINE.OPPORTUNITIES.LOAD_MORE_ERROR') }}
        </p>
        <div
          v-if="hasMore && !loading && !loadFailed"
          class="mt-3 flex justify-center"
        >
          <Button
            variant="outline"
            color="slate"
            size="sm"
            :label="
              appendFailed
                ? t('PIPELINE.OPPORTUNITIES.RETRY')
                : t('PIPELINE.OPPORTUNITIES.LOAD_MORE')
            "
            :is-loading="loadingMore"
            :disabled="loadingMore"
            @click="loadMore"
          />
        </div>
      </div>

      <footer class="border-t border-n-weak px-4 py-4 sm:px-6">
        <Button
          variant="outline"
          color="slate"
          size="lg"
          class="w-full"
          :label="t('PIPELINE.OPPORTUNITIES.CLOSE')"
          @click="emit('close')"
        />
      </footer>
    </section>
  </Modal>
</template>
